/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import {
    ClusterService,
    IpGroupApiService,
    IpGroupModel,
    NetworkingApiService,
    NetworkingService,
    NetworkInterfaceModel
} from '../../../shared';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LanguageService, LogService, ModalService, NavigationService, RbacService } from '../../../../../core/services';
import { Router } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ips-groups',
    templateUrl: './ip-groups.component.html',
    styleUrls: ['./ip-groups.component.scss']
})

export class IpGroupsComponent extends BaseComponent {
    /**
     * To store the group's data.
     * @private
     */
    public groups: IpGroupModel[] = [];

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the interface's data.
     * @private
     */
    private interfaces: NetworkInterfaceModel[];

    constructor(
        private clusterService: ClusterService,
        private ipGroupApiService: IpGroupApiService,
        private languageService: LanguageService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private networkingApiService: NetworkingApiService,
        private networkingService: NetworkingService,
        private rbacService: RbacService,
        private router: Router
    ) {
        super();

        this.subscribeToPageEmitters();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Open edit group modal.
     * @param row
     */
    public openEditGroup(row: IpGroupModel): void {
        this.navigateToURL(`/network/ip-groups/edit/${row.id}`);
    }

    /**
     * Confirm delete ip group.
     * @param group
     */
    public confirmDeleteGroup(group: IpGroupModel): void {
        const confirmationObject = {
            title: 'delete_group',
            message: 'ip_delete_group_text',
            callBack: this.deleteGroup.bind(this, group),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Set up the data for the page.
     * @private
     */
    private setUpPageData(): void {
        forkJoin({
            groups: this.ipGroupApiService.getGroups(),
            interfaces: this.networkingApiService.getInterfaces()
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['interfaces']?.length && dataGroup['groups']?.length) {
                        this.groups = dataGroup['groups'];
                        this.interfaces = dataGroup['interfaces'];
                        this.populateTableRows();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('IpGroupsComponent:setUpPageData() error', error.message);
                }
            });
    }

    /**
     * Populate the two tables from the incoming data.
     * @private
     */
    private populateTableRows(): void {
        // Reset our rows variable
        this.tableRows = [];

        // Populate our rows with group data
        for (const group of this.groups) {
            const assignedInterface = this.networkingService.getLocalAssignedInterface(group, this.interfaces);
            this.tableRows.push({
                id: group.id,
                name: group.name,
                networkIp: this.formatNetworkAddress(group),
                ipRange: group.ipRange,
                gateway: group.gateway,
                defaultGateway: group.defaultGateway,
                assignedInterfaces: this.getAssignedInterfaceText(assignedInterface),
                usage: group.usage
            });
        }
    }

    /**
     * Format network address.
     * @param group
     * @private
     */
    private formatNetworkAddress(group: any): string {
        return group.networkIp + '/' + group.networkMask.toString();
    }

    /**
     * Subscribe to page emitters.
     * @private
     */
    private subscribeToPageEmitters(): void {
        this.clusterService.nodesEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel) => {
                    this.logService.error('IpGroupsComponent: constructor', 'Unable to get floating IP groups' + error);
                }
            });
    }

    /**
     * Delete Group.
     * @param group
     * @private
     */
    private deleteGroup(group: IpGroupModel): Observable<any> {
        return new Observable((observer) => {
            this.ipGroupApiService.deleteGroup(
                group.id
            ).subscribe({
                next: (): void => {
                    this.setUpPageData();
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('IpGroupsComponent:deleteGroup() error', error.message);
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }

    /**
     * Get the assigned interface text.
     * @param assignedInterface
     * @private
     */
    private getAssignedInterfaceText(assignedInterface: NetworkInterfaceModel): string {
        if (!assignedInterface) {
            return this.languageService.translate('ip_groups_unassigned_group');
        } else {
            return `${assignedInterface?.name} (${assignedInterface?.settings.interfaceName})`;
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'ip-groups-add-group': [{ path: '/ipgroups', methods: ['POST'] }],
            'ip-groups-edit-group': [{ path: '/ipgroups/:id', methods: ['PUT'] }],
            'ip-groups-delete-group': [{ path: '/ipgroups/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
